/**
 * Product List Cache Manager
 * Handles caching of product lists with pagination support
 * Automatically clears cache on browser hard-refresh
 */
class ProductListCacheManager {
  constructor() {
    this.cacheKey = 'productListCache';
    this.maxAge = 30 * 60 * 1000; // 30 minutes
    this.maxCacheSize = 50; // Maximum number of cached pages
    
    // Only initialize on client side
    if (typeof window !== 'undefined') {
      try {
        this.init();
      } catch (error) {
        console.error('Failed to initialize ProductListCacheManager:', error);
        // Continue without initialization
      }
    }
  }

  /**
   * Initialize cache manager and detect hard refresh
   */
  init() {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      // Detect hard refresh vs soft navigation
      this.detectHardRefreshImproved();
      
      // Clear expired cache entries
      this.cleanupExpiredCache();
    } catch (error) {
      console.error('Error during cache initialization:', error);
      // Continue without full initialization
    }
  }

  /**
   * Improved hard refresh detection using multiple reliable methods
   */
  detectHardRefreshImproved() {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      // Method 1: Check if this is the first load of the page
      const isFirstLoad = !sessionStorage.getItem('pageLoaded');
      if (isFirstLoad) {
        sessionStorage.setItem('pageLoaded', 'true');
        // This is the first load, but not necessarily a hard refresh
        // We'll use other methods to determine if it's actually a hard refresh
      }
      
      // Method 2: Check if we have a previous page reference
      const previousPage = sessionStorage.getItem('previousPage');
      const currentPage = window.location.href;
      
      if (previousPage && previousPage !== currentPage) {
        // This is navigation (back/forward), NOT a hard refresh
        console.log('🔄 Navigation detected (not hard refresh) - preserving cache');
        // Update page tracking
        sessionStorage.setItem('previousPage', currentPage);
        return; // Don't clear cache on navigation
      } else if (previousPage === currentPage) {
        // Same page, could be a hard refresh
        // Use the new browser reload detection instead of time-based
        if (this.detectBrowserReload()) {
          console.log('🔄 Browser reload detected - clearing cache');
          this.clearAllCache();
          return;
        } else {
          console.log('🔄 Same page navigation - preserving cache');
        }
      } else {
        // No previous page, could be first visit or hard refresh
        // Use the new browser reload detection
        if (this.detectBrowserReload()) {
          console.log('🔄 Browser reload detected - clearing cache');
          this.clearAllCache();
          return;
        } else {
          // No reload detected, this is likely a first visit
          console.log('🔄 First visit - preserving cache');
        }
      }
      
      // Update page tracking
      sessionStorage.setItem('previousPage', currentPage);
      sessionStorage.setItem('lastReloadTime', Date.now().toString());
      
    } catch (error) {
      console.warn('Cache: Error detecting hard refresh:', error);
      // Don't clear cache on error
    }
  }

  /**
   * Detect if this is a hard refresh (F5, Ctrl+R, browser reload button)
   */
  detectBrowserReload() {
    // Only run on client side
    if (typeof window === 'undefined') {
      return false;
    }
    
    try {
      // Method 1: Use performance.navigation.type (most reliable)
      if (performance && performance.navigation) {
        // performance.navigation.type values:
        // 0 = Navigation started by clicking on a link, or entering the URL in the user agent's address bar, or form submission, or initializing a script, or any of the above
        // 1 = Navigation started by clicking the reload button, or pressing F5, or calling location.reload()
        // 2 = Navigation started by moving through the browser history
        if (performance.navigation.type === 1) {
          console.log('🔄 Browser reload button detected via performance.navigation.type');
          return true;
        } else if (performance.navigation.type === 2) {
          console.log('🔄 Browser history navigation detected (back/forward) - NOT a reload');
          return false;
        }
      }
      
      // Method 2: Check if page was loaded from cache (but only if it's not navigation)
      if (performance && performance.getEntriesByType) {
        const navigationEntries = performance.getEntriesByType('navigation');
        if (navigationEntries.length > 0) {
          const navigation = navigationEntries[0];
          // Check if page was loaded from cache (indicates reload)
          if (navigation.transferSize === 0 && navigation.encodedBodySize === 0) {
            // But first check if this is navigation, not reload
            if (performance.navigation && performance.navigation.type === 2) {
              console.log('🔄 Page loaded from cache but it\'s navigation - NOT a reload');
              return false;
            }
            console.log('🔄 Page loaded from cache - likely browser reload');
            return true;
          }
        }
      }
      
      // Method 3: Check if this is a reload by comparing with previous state
      const previousState = sessionStorage.getItem('pageState');
      const currentState = JSON.stringify({
        url: window.location.href,
        timestamp: Date.now(),
        referrer: document.referrer
      });
      
      if (previousState) {
        const prevStateObj = JSON.parse(previousState);
        // If same URL but different timestamp and no referrer change, likely reload
        // But only if it's not navigation
        if (prevStateObj.url === window.location.href && 
            prevStateObj.referrer === document.referrer &&
            (Date.now() - prevStateObj.timestamp) < 5000) {
          // Check if this is navigation
          if (performance.navigation && performance.navigation.type === 2) {
            console.log('🔄 Same page but it\'s navigation - NOT a reload');
            return false;
          }
          console.log('🔄 Same page reload detected via state comparison');
          return true;
        }
      }
      
      // Save current state for next comparison
      sessionStorage.setItem('pageState', currentState);
      
      return false;
      
    } catch (error) {
      console.warn('Cache: Error detecting browser reload:', error);
      return false;
    }
  }

  /**
   * Detect if this is a hard refresh (F5, Ctrl+R, browser reload button)
   */
  detectHardRefresh() {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      // Method 1: Use the more reliable browser reload detection
      if (this.detectBrowserReload()) {
        console.log('🔄 Browser reload detected via detectBrowserReload - clearing cache');
        this.clearAllCache();
        return;
      }
      
      // Method 2: Check if this is the first load of the page
      const isFirstLoad = !sessionStorage.getItem('pageLoaded');
      if (isFirstLoad) {
        sessionStorage.setItem('pageLoaded', 'true');
        console.log('🔄 First page load - preserving cache');
        return; // Don't clear cache on first load
      }
      
      // Method 3: Check if we have a previous page reference
      const previousPage = sessionStorage.getItem('previousPage');
      const currentPage = window.location.href;
      
      if (previousPage && previousPage !== currentPage) {
        // This is navigation (back/forward), NOT a hard refresh
        console.log('🔄 Navigation detected (not hard refresh) - preserving cache');
        // Update page tracking
        sessionStorage.setItem('previousPage', currentPage);
        return; // Don't clear cache on navigation
      } else if (previousPage === currentPage) {
        // Same page, could be a hard refresh
        // Check if the page was reloaded using performance API
        if (performance && performance.navigation && performance.navigation.type === 1) {
          console.log('🔄 Hard refresh detected via performance API - clearing cache');
          this.clearAllCache();
          return;
        } else {
          console.log('🔄 Same page navigation - preserving cache');
        }
      } else {
        // No previous page, could be first visit or hard refresh
        // Check if this is a reload using performance API
        if (performance && performance.navigation && performance.navigation.type === 1) {
          console.log('🔄 Hard refresh detected via performance API - clearing cache');
          this.clearAllCache();
          return;
        } else {
          // No reload detected, this is likely a first visit
          console.log('🔄 First visit - preserving cache');
        }
      }
      
      // Update page tracking
      sessionStorage.setItem('previousPage', currentPage);
      sessionStorage.setItem('lastReloadTime', Date.now().toString());
      
    } catch (error) {
      console.warn('Cache: Error detecting hard refresh:', error);
      // Don't clear cache on error
    }
  }

  /**
   * Save product list data to cache
   * @param {string} cacheKey - Unique key for this cache entry
   * @param {Object} data - Product list data
   * @param {number} page - Page number
   * @param {Object} filters - Applied filters
   * @param {Object} scrollPosition - Current scroll position
   */
  saveToCache(cacheKey, data, page = 1, filters = {}, scrollPosition = null) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return false;
    }
    
    try {
      // Get current scroll position if not provided
      if (!scrollPosition) {
        scrollPosition = this.getCurrentScrollPosition();
      }

      // Normalize filters before storing to ensure consistent format
      const normalizedFilters = this.normalizeFilters(filters);
      
      console.log('💾 Saving to cache with normalized filters:', normalizedFilters, 'from:', filters);

      const cacheData = {
        data: data,
        page: page,
        filters: normalizedFilters, // Store normalized filters
        scrollPosition: scrollPosition,
        timestamp: Date.now(),
        totalPages: data.totalPages || 1,
        totalItems: data.totalItems || 0
      };

      const cache = this.getCache();
      
      // Store by cache key and page
      if (!cache[cacheKey]) {
        cache[cacheKey] = {};
      }
      
      cache[cacheKey][page] = cacheData;
      
      // Limit cache size
      this.limitCacheSize(cache);
      
      // Save to sessionStorage
      sessionStorage.setItem(this.cacheKey, JSON.stringify(cache));
      
      console.log(`💾 Cached product list: ${cacheKey}, page ${page}, scroll: ${scrollPosition.y}`);
      return true;
    } catch (error) {
      console.error('Failed to save to cache:', error);
      return false;
    }
  }

  /**
   * Get product list data from cache
   * @param {string} cacheKey - Unique key for this cache entry
   * @param {number} page - Page number
   * @param {Object} filters - Applied filters to check if cache is still valid
   * @param {boolean} restoreScroll - Whether to restore scroll position
   */
  getFromCache(cacheKey, page = 1, filters = {}, restoreScroll = false) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return null;
    }
    
    try {
      const cache = this.getCache();
      
      if (!cache[cacheKey] || !cache[cacheKey][page]) {
        return null;
      }
      
      const cachedData = cache[cacheKey][page];
      
      // Check if cache is expired
      if (Date.now() - cachedData.timestamp > this.maxAge) {
        this.removeFromCache(cacheKey, page);
        return null;
      }
      
      // Check if filters match (basic comparison)
      if (!this.filtersMatch(cachedData.filters, filters)) {
        this.removeFromCache(cacheKey, page);
        return null;
      }
      
      console.log(`Retrieved from cache: ${cacheKey}, page ${page}`);
      
      // Restore scroll position if requested
      if (restoreScroll && cachedData.scrollPosition) {
        this.restoreScrollPosition(cachedData.scrollPosition);
      }
      
      return cachedData.data;
    } catch (error) {
      console.error('Failed to get from cache:', error);
      return null;
    }
  }

  /**
   * Get cached data with automatic scroll restoration
   * @param {string} cacheKey - Unique key for this cache entry
   * @param {number} page - Page number
   * @param {Object} filters - Applied filters
   */
  getFromCacheWithScroll(cacheKey, page = 1, filters = {}) {
    return this.getFromCache(cacheKey, page, filters, true);
  }

  /**
   * Get complete cached data including scroll position (without auto-restore)
   * @param {string} cacheKey - Unique key for this cache entry
   * @param {number} page - Page number
   * @param {Object} filters - Applied filters
   */
  getCompleteCachedData(cacheKey, page = 1, filters = {}) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return null;
    }
    
    try {
      const cache = this.getCache();
      
      if (!cache[cacheKey] || !cache[cacheKey][page]) {
        return null;
      }
      
      const cachedData = cache[cacheKey][page];
      
      // Check if cache is expired
      if (Date.now() - cachedData.timestamp > this.maxAge) {
        this.removeFromCache(cacheKey, page);
        return null;
      }
      
      // Check if filters match (basic comparison)
      if (!this.filtersMatch(cachedData.filters, filters)) {
        this.removeFromCache(cacheKey, page);
        return null;
      }
      
      console.log(`Retrieved complete cached data: ${cacheKey}, page ${page}`);
      
      // Return complete cached data including scroll position
      return {
        products: cachedData.data,
        scrollPosition: cachedData.scrollPosition,
        totalPages: cachedData.totalPages,
        totalItems: cachedData.totalItems,
        hasReachedFullProductPages: cachedData.hasReachedFullProductPages,
        allowToLoadMore: cachedData.allowToLoadMore,
        timestamp: cachedData.timestamp,
        filters: cachedData.filters
      };
    } catch (error) {
      console.error('Failed to get complete cached data:', error);
      return null;
    }
  }

  /**
   * Smart cache retrieval with automatic product display and scroll restoration
   * @param {string} cacheKey - Unique key for this cache entry
   * @param {number} page - Page number
   * @param {Object} filters - Applied filters
   * @param {Object} displayOptions - Options for display and scroll restoration
   */
  smartCacheRetrieval(cacheKey, page = 1, filters = {}, displayOptions = {}) {
    const {
      autoRestoreScroll = true,
      autoDisplayProducts = true,
      onDataRetrieved = null,
      onScrollRestored = null,
      onComplete = null
    } = displayOptions;

    // Get complete cached data
    const cachedData = this.getCompleteCachedData(cacheKey, page, filters);
    
    if (!cachedData) {
      return { success: false, reason: 'No cached data found' };
    }

    try {
      // STEP 1: Prevent any scroll interference immediately
      if (autoRestoreScroll && cachedData.scrollPosition) {
        this.preventScrollInterference(2000);
        this.preventScrollToTop(3000);
        this.disableAutoScrolling(3000);
        console.log('🛡️ Scroll protection enabled for cache restoration');
      }

      // STEP 2: Auto-display products if requested (this should happen first)
      if (autoDisplayProducts && onDataRetrieved) {
        console.log('📊 Displaying cached products first...');
        onDataRetrieved(cachedData);
        
        // Wait longer for the DOM to be fully updated and page to stabilize
        setTimeout(() => {
          console.log('✅ Products displayed, waiting for page to stabilize...');
          
          // Wait a bit more for any other page components to load
          setTimeout(() => {
            console.log('🎯 Page stabilized, now restoring scroll...');
            
            // STEP 3: Restore scroll position after products are displayed and page is stable
            if (autoRestoreScroll && cachedData.scrollPosition) {
              this.restoreScrollPosition(cachedData.scrollPosition, null, 50);
              
              if (onScrollRestored) {
                onScrollRestored(cachedData.scrollPosition);
              }
            }
          }, 300); // Wait 300ms for page to stabilize
        }, 200); // Wait 200ms for DOM update
      } else if (autoRestoreScroll && cachedData.scrollPosition) {
        // If no auto-display, just restore scroll with longer delay
        setTimeout(() => {
          this.restoreScrollPosition(cachedData.scrollPosition, null, 500); // Wait 500ms for page to be ready
          
          if (onScrollRestored) {
            onScrollRestored(cachedData.scrollPosition);
          }
        }, 500); // Wait 500ms for page to be ready
      }

      // STEP 4: Set up automatic scroll saving for this cache entry
      this.setupScrollSaving(cacheKey, page);

      if (onComplete) {
        onComplete(cachedData);
      }

      return { 
        success: true, 
        data: cachedData,
        message: 'Cached data displayed and scroll position restored'
      };

    } catch (error) {
      console.error('Error during smart cache retrieval:', error);
      return { success: false, reason: error.message };
    }
  }

  /**
   * One-click cache restoration - automatically displays products and restores scroll
   * @param {string} cacheKey - Unique key for this cache entry
   * @param {number} page - Page number
   * @param {Object} filters - Applied filters
   * @param {Function} displayCallback - Function to call with cached data
   */
  oneClickRestore(cacheKey, page = 1, filters = {}, displayCallback) {
    if (typeof displayCallback !== 'function') {
      console.error('displayCallback must be a function');
      return { success: false, reason: 'Invalid callback function' };
    }

    return this.smartCacheRetrieval(cacheKey, page, filters, {
      autoRestoreScroll: true,
      autoDisplayProducts: true,
      onDataRetrieved: displayCallback,
      onScrollRestored: (scrollPosition) => {
        console.log('✨ Scroll position restored to:', scrollPosition);
      },
      onComplete: (cachedData) => {
        console.log('🎉 Cache restoration completed successfully!');
      }
    });
  }

  /**
   * Quick cache check - returns true if cache exists and is valid
   * @param {string} cacheKey - Unique key for this cache entry
   * @param {number} page - Page number
   * @param {Object} filters - Applied filters
   */
  hasValidCache(cacheKey, page = 1, filters = {}) {
    const cachedData = this.getCompleteCachedData(cacheKey, page, filters);
    return cachedData !== null;
  }

  /**
   * Check if we should use cache instead of making API call
   * @param {string} cacheKey - Unique key for this cache entry
   * @param {number} page - Page number
   * @param {Object} filters - Applied filters
   * @param {boolean} forceRefresh - Force API call even if cache exists
   */
  shouldUseCache(cacheKey, page = 1, filters = {}, forceRefresh = false) {
    if (forceRefresh) {
      return false;
    }
    
    if (!this.isCacheAvailable()) {
      return false;
    }
    
    return this.hasValidCache(cacheKey, page, filters);
  }

  /**
   * Get detailed cache information for debugging
   * @param {string} cacheKey - Unique key for this cache entry
   * @param {number} page - Page number
   * @param {Object} filters - Applied filters
   */
  getCacheInfo(cacheKey, page = 1, filters = {}) {
    const cachedData = this.getCompleteCachedData(cacheKey, page, filters);
    
    if (!cachedData) {
      return {
        exists: false,
        reason: 'No cache found or cache invalid'
      };
    }
    
    const age = Date.now() - cachedData.timestamp;
    const ageMinutes = Math.floor(age / (1000 * 60));
    
    return {
      exists: true,
      age: `${ageMinutes} minutes`,
      ageMs: age,
      productsCount: cachedData.products?.length || 0,
      hasScrollPosition: !!cachedData.scrollPosition,
      scrollPosition: cachedData.scrollPosition,
      totalPages: cachedData.totalPages,
      totalItems: cachedData.totalItems,
      filters: cachedData.filters,
      timestamp: new Date(cachedData.timestamp).toLocaleString()
    };
  }

  /**
   * Debug method to test cache functionality
   * @param {string} testKey - Test cache key
   */
  debugCache(testKey = 'test_debug') {
    // Only run on client side
    if (typeof window === 'undefined') {
      return { error: 'Not available on server side' };
    }
    
    try {
      console.log('🔍 Debugging cache system...');
      
      // Test basic functionality
      const isAvailable = this.isCacheAvailable();
      const isInit = this.isInitialized();
      const status = this.getStatus();
      
      console.log('Cache status:', {
        isAvailable,
        isInitialized: isInit,
        status
      });
      
      // Test cache operations
      const testData = {
        data: [{ id: 1, name: 'Test Product' }],
        totalPages: 1,
        totalItems: 1,
        hasReachedFullProductPages: false,
        allowToLoadMore: false
      };
      
      // Save test data
      this.saveToCache(testKey, testData, 1, { test: true }, { x: 0, y: 100 });
      console.log('✅ Test data saved to cache');
      
      // Retrieve test data
      const retrieved = this.getCompleteCachedData(testKey, 1, { test: true });
      console.log('✅ Test data retrieved:', retrieved ? 'SUCCESS' : 'FAILED');
      
      // Test smart retrieval
      const smartResult = this.smartCacheRetrieval(testKey, 1, { test: true }, {
        autoRestoreScroll: false,
        autoDisplayProducts: false,
        onDataRetrieved: (data) => console.log('🎯 onDataRetrieved callback executed'),
        onComplete: (data) => console.log('🎉 onComplete callback executed')
      });
      console.log('✅ Smart retrieval test:', smartResult);
      
      // Clean up test data
      this.removeFromCache(testKey, 1);
      console.log('✅ Test data cleaned up');
      
      return {
        success: true,
        message: 'Cache system is working correctly',
        details: {
          isAvailable,
          isInitialized: isInit,
          status,
          smartRetrieval: smartResult
        }
      };
      
    } catch (error) {
      console.error('❌ Cache debug failed:', error);
      return {
        success: false,
        error: error.message,
        stack: error.stack
      };
    }
  }

  /**
   * Normalize filter object to ensure consistent format
   * Extracts all filters from filterQuery string and merges with individual properties
   * @param {Object} filters - Filter object that may contain filterQuery string and/or individual properties
   * @returns {Object} Normalized filter object with consistent format
   */
  normalizeFilters(filters = {}) {
    if (!filters || typeof filters !== 'object') {
      return {};
    }

    const normalized = {};
    
    // Step 1: Parse filterQuery string if present to extract all parameters
    // Also check for 'query' key (used by WithFilter component)
    const filterQueryString = filters.filterQuery || filters.query || '';
    if (filterQueryString && typeof filterQueryString === 'string' && filterQueryString.trim() !== '') {
      try {
        // Parse the query string (format: "key1=value1&key2=value2&attribute_ids[]=123")
        const queryParams = new URLSearchParams(filterQueryString);
        
        // Extract all parameters from filterQuery
        for (const [key, value] of queryParams.entries()) {
          // Handle array parameters like attribute_ids[]
          if (key.includes('[]') || key.includes('[')) {
            const arrayKey = key.replace(/\[\d*\]/g, '').replace('[]', '');
            if (!normalized[arrayKey]) {
              normalized[arrayKey] = [];
            }
            // Add value to array (avoid duplicates)
            const parsedValue = decodeURIComponent(value);
            if (!normalized[arrayKey].includes(parsedValue)) {
              normalized[arrayKey].push(parsedValue);
            }
          } else {
            // Regular parameter
            const decodedValue = decodeURIComponent(value);
            normalized[key] = decodedValue;
          }
        }
        
        // Sort array values for consistency
        Object.keys(normalized).forEach(key => {
          if (Array.isArray(normalized[key])) {
            normalized[key].sort();
          }
        });
      } catch (error) {
        console.warn('Failed to parse filterQuery:', error);
      }
    }
    
    // Step 2: Merge individual filter properties (these take precedence over filterQuery)
    // Map individual properties to their normalized names
    const propertyMap = {
      search: 'search',
      category: 'category_id',
      categoryId: 'category_id',
      attributes: 'attribute_ids',
      attributesQuery: 'attribute_ids',
      store: 'store_id',
      storeId: 'store_id',
      governorate: 'governorate_id',
      governorateId: 'governorate_id',
      area: 'area_id',
      areaId: 'area_id',
      priceMin: 'min_price',
      minPrice: 'min_price',
      priceMax: 'max_price',
      maxPrice: 'max_price',
      spaceMin: 'min_space',
      minSpace: 'min_space',
      spaceMax: 'max_space',
      maxSpace: 'max_space',
      isNegotiable: 'is_negotiable',
      date: 'sort_type',
      sortBy: 'sort_by',
      sortType: 'sort_type',
    };
    
    Object.keys(filters).forEach(key => {
      // Skip filterQuery and query as we've already parsed them
      if (key === 'filterQuery' || key === 'query') return;
      
      const value = filters[key];
      
      // Skip null, undefined, and empty strings (unless it's a boolean false)
      if (value === null || value === undefined || (value === '' && typeof value !== 'boolean')) {
        return;
      }
      
      // Map property name to normalized name
      const normalizedKey = propertyMap[key] || key;
      
      // Handle special cases
      if (normalizedKey === 'attribute_ids' || normalizedKey === 'attributes') {
        // Handle attributes - can be string (comma-separated) or array
        let attrIds = [];
        if (typeof value === 'string' && value.trim() !== '') {
          attrIds = value.split(/[&,]/).map(id => id.trim()).filter(id => id !== '');
        } else if (Array.isArray(value)) {
          attrIds = value.map(id => String(id)).filter(id => id !== '');
        }
        
        if (attrIds.length > 0) {
          // Merge with existing attribute_ids from filterQuery
          if (!normalized.attribute_ids) {
            normalized.attribute_ids = [];
          }
          // Combine and deduplicate
          const combined = [...new Set([...normalized.attribute_ids, ...attrIds])];
          normalized.attribute_ids = combined.sort();
        }
      } else if (normalizedKey === 'category_id' || normalizedKey === 'category') {
        // Normalize category - convert "0" to empty string
        const categoryValue = String(value).trim();
        if (categoryValue && categoryValue !== '0') {
          normalized.category_id = categoryValue;
        }
      } else if (normalizedKey === 'is_negotiable') {
        // Normalize boolean
        normalized.is_negotiable = value === true || value === 'true' || value === 1 || value === '1';
      } else {
        // Regular property - convert to string and trim
        const stringValue = String(value).trim();
        if (stringValue !== '') {
          normalized[normalizedKey] = stringValue;
        }
      }
    });
    
    // Step 3: Normalize empty values and ensure consistent defaults
    // Convert empty strings to consistent format (empty string for strings, false for booleans)
    Object.keys(normalized).forEach(key => {
      if (normalized[key] === null || normalized[key] === undefined) {
        delete normalized[key];
      } else if (Array.isArray(normalized[key]) && normalized[key].length === 0) {
        delete normalized[key];
      } else if (typeof normalized[key] === 'string' && normalized[key].trim() === '') {
        delete normalized[key];
      }
    });
    
    // Step 4: Sort keys for consistent comparison
    const sortedKeys = Object.keys(normalized).sort();
    const result = {};
    sortedKeys.forEach(key => {
      result[key] = normalized[key];
    });
    
    console.log('🔧 Normalized filters:', result, 'from input:', filters);
    return result;
  }

  /**
   * Check if filters match between cached and current filters
   * @param {Object} cachedFilters - Filters from cache
   * @param {Object} currentFilters - Current applied filters
   */
  filtersMatch(cachedFilters, currentFilters) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return false;
    }
    
    if (!cachedFilters || !currentFilters) {
      return false;
    }
    
    // Normalize both filter objects before comparison
    const normalizedCached = this.normalizeFilters(cachedFilters);
    const normalizedCurrent = this.normalizeFilters(currentFilters);
    
    // Compare normalized filters
    const cachedStr = JSON.stringify(normalizedCached);
    const currentStr = JSON.stringify(normalizedCurrent);
    
    const matches = cachedStr === currentStr;
    
    if (!matches) {
      console.log('❌ Filter mismatch:', {
        cached: normalizedCached,
        current: normalizedCurrent,
        cachedStr,
        currentStr
      });
    } else {
      console.log('✅ Filters match:', normalizedCached);
    }
    
    return matches;
  }

  /**
   * Remove specific cache entry
   * @param {string} cacheKey - Cache key to remove
   * @param {number} page - Page number to remove
   */
  removeFromCache(cacheKey, page) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      const cache = this.getCache();
      
      if (cache[cacheKey] && cache[cacheKey][page]) {
        delete cache[cacheKey][page];
        
        // Remove empty cache keys
        if (Object.keys(cache[cacheKey]).length === 0) {
          delete cache[cacheKey];
        }
        
        sessionStorage.setItem(this.cacheKey, JSON.stringify(cache));
        console.log(`Removed from cache: ${cacheKey}, page ${page}`);
      }
    } catch (error) {
      console.error('Failed to remove from cache:', error);
    }
  }

  /**
   * Clear all cache for a specific key
   * @param {string} cacheKey - Cache key to clear
   */
  clearCacheByKey(cacheKey) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      const cache = this.getCache();
      
      if (cache[cacheKey]) {
        delete cache[cacheKey];
        sessionStorage.setItem(this.cacheKey, JSON.stringify(cache));
        console.log(`Cleared cache for key: ${cacheKey}`);
      }
    } catch (error) {
      console.error('Failed to clear cache by key:', error);
    }
  }

  /**
   * Clear all cache
   */
  clearAllCache() {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      sessionStorage.removeItem(this.cacheKey);
      console.log('All cache cleared');
    } catch (error) {
      console.error('Failed to clear all cache:', error);
    }
  }

  /**
   * Clear cache but preserve product positions for a specific context
   * @param {string} preserveCacheKey - Cache key to preserve positions for
   * @param {number} preservePage - Page number to preserve positions for
   */
  clearCachePreserveProductPositions(preserveCacheKey, preservePage) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      const cache = this.getCache();
      
      // Extract product positions to preserve
      let preservedPositions = null;
      if (preserveCacheKey && preservePage && 
          cache[preserveCacheKey] && 
          cache[preserveCacheKey][preservePage] &&
          cache[preserveCacheKey][preservePage].productPositions) {
        preservedPositions = cache[preserveCacheKey][preservePage].productPositions;
        console.log(`💾 Preserving product positions for ${preserveCacheKey}, page ${preservePage}`);
      }
      
      // Clear all cache
      sessionStorage.removeItem(this.cacheKey);
      
      // Restore preserved positions if they exist
      if (preservedPositions) {
        const newCache = {};
        if (!newCache[preserveCacheKey]) {
          newCache[preserveCacheKey] = {};
        }
        if (!newCache[preserveCacheKey][preservePage]) {
          newCache[preserveCacheKey][preservePage] = {};
        }
        newCache[preserveCacheKey][preservePage].productPositions = preservedPositions;
        sessionStorage.setItem(this.cacheKey, JSON.stringify(newCache));
        console.log(`✅ Product positions preserved and restored`);
      }
      
      console.log('Cache cleared (product positions preserved)');
    } catch (error) {
      console.error('Failed to clear cache with preserved positions:', error);
      // Fallback to regular clear
      this.clearAllCache();
    }
  }

  /**
   * Get cache from sessionStorage
   */
  getCache() {
    // Only run on client side
    if (typeof window === 'undefined') {
      return {};
    }
    
    try {
      const cached = sessionStorage.getItem(this.cacheKey);
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      console.error('Failed to get cache:', error);
      return {};
    }
  }

  /**
   * Limit cache size to prevent memory issues
   * @param {Object} cache - Cache object
   */
  limitCacheSize(cache) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    const allEntries = [];
    
    // Collect all cache entries with their keys and timestamps
    Object.keys(cache).forEach(cacheKey => {
      Object.keys(cache[cacheKey]).forEach(page => {
        allEntries.push({
          cacheKey,
          page,
          timestamp: cache[cacheKey][page].timestamp
        });
      });
    });
    
    // If cache is too large, remove oldest entries
    if (allEntries.length > this.maxCacheSize) {
      // Sort by timestamp (oldest first)
      allEntries.sort((a, b) => a.timestamp - b.timestamp);
      
      // Remove oldest entries
      const entriesToRemove = allEntries.slice(0, allEntries.length - this.maxCacheSize);
      
      entriesToRemove.forEach(entry => {
        this.removeFromCache(entry.cacheKey, entry.page);
      });
      
      console.log(`Cache size limited: removed ${entriesToRemove.length} old entries`);
    }
  }

  /**
   * Clean up expired cache entries
   */
  cleanupExpiredCache() {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      const cache = this.getCache();
      let cleanedCount = 0;
      
      Object.keys(cache).forEach(cacheKey => {
        Object.keys(cache[cacheKey]).forEach(page => {
          const cachedData = cache[cacheKey][page];
          
          if (Date.now() - cachedData.timestamp > this.maxAge) {
            this.removeFromCache(cacheKey, page);
            cleanedCount++;
          }
        });
      });
      
      if (cleanedCount > 0) {
        console.log(`Cleaned up ${cleanedCount} expired cache entries`);
      }
    } catch (error) {
      console.error('Failed to cleanup expired cache:', error);
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    // Only run on client side
    if (typeof window === 'undefined') {
      return null;
    }
    
    try {
      const cache = this.getCache();
      let totalEntries = 0;
      let totalSize = 0;
      
      Object.keys(cache).forEach(cacheKey => {
        Object.keys(cache[cacheKey]).forEach(page => {
          totalEntries++;
          totalSize += JSON.stringify(cache[cacheKey][page]).length;
        });
      });
      
      return {
        totalEntries,
        totalSize: `${(totalSize / 1024).toFixed(2)} KB`,
        cacheKeys: Object.keys(cache),
        maxAge: `${(this.maxAge / 1000 / 60).toFixed(0)} minutes`
      };
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return null;
    }
  }

  /**
   * Generate cache key based on filters and sorting
   * @param {Object} filters - Applied filters
   * @param {string} sortBy - Sorting method
   * @param {string} productType - Product type filter
   */
  generateCacheKey(filters = {}, sortBy = 'all', productType = null) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return 'server_side_cache_key';
    }
    
    // Normalize filters first to ensure consistent format
    const normalizedFilters = this.normalizeFilters(filters);
    
    const keyParts = [
      'products',
      sortBy || 'all',
      productType?.value || 'all'
    ];
    
    // Generate key from normalized filters only (no dual filterQuery + individual properties)
    // Sort keys for consistent key generation
    const filterKeys = Object.keys(normalizedFilters).sort();
    filterKeys.forEach(key => {
      const value = normalizedFilters[key];
      
      // Skip empty values
      if (value === null || value === undefined || value === '') {
        return;
      }
      
      // Handle array values (like attribute_ids)
      if (Array.isArray(value)) {
        if (value.length > 0) {
          // Sort array values for consistency
          const sortedArray = [...value].sort();
          keyParts.push(`${key}:${sortedArray.join(',')}`);
        }
      } else {
        // Regular value
        keyParts.push(`${key}:${value}`);
      }
    });
    
    const generatedKey = keyParts.join('_');
    console.log('🔑 Generated cache key:', generatedKey);
    console.log('   From normalized filters:', normalizedFilters);
    console.log('   Original filters:', filters);
    return generatedKey;
  }

  /**
   * Check if cache is available and enabled
   */
  isCacheAvailable() {
    // Only run on client side
    if (typeof window === 'undefined') {
      return false;
    }
    
    try {
      return 'sessionStorage' in window && sessionStorage !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if cache manager is properly initialized
   */
  isInitialized() {
    return typeof window !== 'undefined' && this.cacheKey && this.maxAge;
  }

  /**
   * Get initialization status
   */
  getStatus() {
    return {
      isClient: typeof window !== 'undefined',
      isInitialized: this.isInitialized(),
      isCacheAvailable: this.isCacheAvailable(),
      cacheKey: this.cacheKey,
      maxAge: this.maxAge,
      maxCacheSize: this.maxCacheSize
    };
  }

  /**
   * Test cache functionality
   */
  testCache() {
    try {
      if (!this.isCacheAvailable()) {
        return { success: false, error: 'Cache not available' };
      }

      const testKey = 'test_cache_key';
      const testData = { test: 'data', timestamp: Date.now() };
      
      // Test save
      const saveResult = this.saveToCache(testKey, testData, 1, { test: true });
      if (!saveResult) {
        return { success: false, error: 'Save failed' };
      }

      // Test get
      const retrievedData = this.getFromCache(testKey, 1, { test: true });
      if (!retrievedData) {
        return { success: false, error: 'Retrieve failed' };
      }

      // Test remove
      this.removeFromCache(testKey, 1);
      
      return { success: true, message: 'Cache test passed' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get current scroll position
   */
  getCurrentScrollPosition() {
    // Only run on client side
    if (typeof window === 'undefined') {
      return { x: 0, y: 0 };
    }
    
    return {
      x: window.scrollX,
      y: window.scrollY
    };
  }

  /**
   * Get scroll position for a specific container element
   * @param {string} containerSelector - CSS selector for the container
   */
  getContainerScrollPosition(containerSelector) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return { x: 0, y: 0 };
    }
    
    try {
      const container = document.querySelector(containerSelector);
      if (container) {
        return {
          x: container.scrollLeft || 0,
          y: container.scrollTop || 0
        };
      }
    } catch (error) {
      console.error('Failed to get container scroll position:', error);
    }
    
    return { x: 0, y: 0 };
  }

  /**
   * Check if the page is ready for scroll restoration
   */
  isPageReadyForScroll() {
    if (typeof window === 'undefined') {
      return false;
    }
    
    try {
      // Check if document is ready
      if (document.readyState !== 'complete') {
        return false;
      }
      
      // Check if there are products visible on the page
      const productElements = document.querySelectorAll('[data-product-id]');
      if (productElements.length === 0) {
        return false;
      }
      
      // Check if the page has loaded enough content
      const bodyHeight = document.body.scrollHeight;
      if (bodyHeight < 500) {
        return false;
      }
      
      return true;
    } catch (error) {
      console.warn('Error checking if page is ready for scroll:', error);
      return false;
    }
  }

  /**
   * Restore scroll position with delay to prevent interference
   * @param {Object} scrollPosition - Scroll position object
   * @param {string} containerSelector - Optional container selector for container scrolling
   * @param {number} delayMs - Delay before restoring scroll (default: 100ms)
   */
  restoreScrollPosition(scrollPosition, containerSelector = null, delayMs = 100) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    if (!scrollPosition) {
      console.log('No scroll position in cache to restore.');
      return;
    }
    
    try {
      // Add a small delay to prevent interference from page initialization
      setTimeout(() => {
        // Check if the page is ready for scrolling
        if (!this.isPageReadyForScroll()) {
          console.log('Page not ready for scroll, retrying in 200ms...');
          // Retry after a bit more delay
          setTimeout(() => {
            this.restoreScrollPosition(scrollPosition, containerSelector, 0);
          }, 200);
          return;
        }
        
        if (containerSelector) {
          // Restore container scroll position
          const container = document.querySelector(containerSelector);
          if (container) {
            container.scrollTo({
              left: scrollPosition.x,
              top: scrollPosition.y,
              behavior: 'instant' // Use instant to avoid smooth scroll interference
            });
            console.log(`Container scroll position restored: x=${scrollPosition.x}, y=${scrollPosition.y}`);
          }
        } else {
          // Restore window scroll position
          window.scrollTo({
            left: scrollPosition.x,
            top: scrollPosition.y,
            behavior: 'instant' // Use instant to avoid smooth scroll interference
          });
          console.log(`Window scroll position restored: x=${scrollPosition.x}, y=${scrollPosition.y}`);
        }
      }, delayMs);
      
    } catch (error) {
      console.error('Failed to restore scroll position:', error);
    }
  }

  /**
   * Update scroll position for existing cache entry
   * @param {string} cacheKey - Cache key to update
   * @param {number} page - Page number
   * @param {Object} scrollPosition - New scroll position
   */
  updateScrollPosition(cacheKey, page, scrollPosition) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return false;
    }
    
    try {
      const cache = this.getCache();
      
      if (cache[cacheKey] && cache[cacheKey][page]) {
        cache[cacheKey][page].scrollPosition = scrollPosition;
        sessionStorage.setItem(this.cacheKey, JSON.stringify(cache));
        console.log(`Updated scroll position for ${cacheKey}, page ${page}: x=${scrollPosition.x}, y=${scrollPosition.y}`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to update scroll position:', error);
      return false;
    }
  }

  /**
   * Save current scroll position to cache
   * @param {string} cacheKey - Cache key to update
   * @param {number} page - Page number
   */
  saveCurrentScrollPosition(cacheKey, page) {
    const scrollPosition = this.getCurrentScrollPosition();
    return this.updateScrollPosition(cacheKey, page, scrollPosition);
  }

  /**
   * Set up automatic scroll position saving
   * @param {string} cacheKey - Cache key to update
   * @param {number} page - Page number
   * @param {number} debounceMs - Debounce time in milliseconds
   * @param {string} containerSelector - Optional container selector for container scrolling
   */
  setupScrollSaving(cacheKey, page, debounceMs = 300, containerSelector = null) {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }
    
    // Remove existing listener if any
    this.removeScrollSaving();
    
    let scrollTimeout;
    let lastSavedPosition = null;
    
    const handleScroll = () => {
      // Don't save scroll if we're preventing interference
      if (this.isPreventingScrollInterference()) {
        return;
      }
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        let currentPosition;
        
        if (containerSelector) {
          // Save container scroll position
          currentPosition = this.getContainerScrollPosition(containerSelector);
        } else {
          // Save window scroll position
          currentPosition = this.getCurrentScrollPosition();
        }
        
        // Only save if position has changed significantly (more than 50px)
        if (!lastSavedPosition || 
            Math.abs(currentPosition.y - lastSavedPosition.y) > 50 ||
            Math.abs(currentPosition.x - lastSavedPosition.x) > 50) {
          
          if (containerSelector) {
            this.updateScrollPosition(cacheKey, page, currentPosition);
          } else {
            this.saveCurrentScrollPosition(cacheKey, page);
          }
          
          lastSavedPosition = { ...currentPosition };
        }
      }, debounceMs);
    };
    
    // Store the handler for later removal
    this.scrollHandler = handleScroll;
    
    // Add scroll event listener
    if (containerSelector) {
      const container = document.querySelector(containerSelector);
      if (container) {
        container.addEventListener('scroll', handleScroll, { passive: true });
      }
    } else {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    console.log(`Scroll saving enabled for ${cacheKey}, page ${page}${containerSelector ? ` (container: ${containerSelector})` : ''}`);
  }

  /**
   * Save scroll position before navigation
   * @param {string} cacheKey - Cache key to update
   * @param {number} page - Page number
   * @param {string} containerSelector - Optional container selector
   */
  saveScrollBeforeNavigation(cacheKey, page, containerSelector = null) {
    if (containerSelector) {
      const containerScroll = this.getContainerScrollPosition(containerSelector);
      this.updateScrollPosition(cacheKey, page, containerScroll);
    } else {
      this.saveCurrentScrollPosition(cacheKey, page);
    }
  }

  /**
   * Remove scroll position saving listener
   */
  removeScrollSaving() {
    // Only run on client side
    if (typeof window === 'undefined' || !this.scrollHandler) {
      return;
    }
    
    window.removeEventListener('scroll', this.scrollHandler);
    this.scrollHandler = null;
    
    console.log('Scroll saving disabled');
  }

  /**
   * Manually clear all cache (for testing or explicit user action)
   */
  manualClearCache() {
    if (typeof window === 'undefined') {
      return false;
    }
    
    try {
      this.clearAllCache();
      console.log('🧹 Cache manually cleared');
      return true;
    } catch (error) {
      console.error('Failed to manually clear cache:', error);
      return false;
    }
  }

  /**
   * Check if we should preserve cache (not a hard refresh)
   */
  shouldPreserveCache() {
    if (typeof window === 'undefined') {
      return true; // Preserve on server side
    }
    
    try {
      // Check if this is a navigation event
      const navigationType = sessionStorage.getItem('navigationType');
      return navigationType === 'navigation';
    } catch (error) {
      return true; // Preserve on error
    }
  }

  /**
   * Mark current action as navigation (not refresh)
   */
  markAsNavigation() {
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      sessionStorage.setItem('navigationType', 'navigation');
      // Clear this after a short delay
      setTimeout(() => {
        sessionStorage.removeItem('navigationType');
      }, 1000);
    } catch (error) {
      console.warn('Failed to mark as navigation:', error);
    }
  }

  /**
   * Set up navigation event listeners to detect back/forward navigation
   */
  setupNavigationDetection() {
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      // Listen for popstate (back/forward navigation)
      const handlePopState = () => {
        console.log('🔄 Browser navigation detected (back/forward) - preserving cache');
        this.markAsNavigation();
      };
      
      // Listen for beforeunload to mark as navigation
      const handleBeforeUnload = () => {
        console.log('🔄 Page unload detected - marking as navigation');
        this.markAsNavigation();
      };
      
      // Add event listeners
      window.addEventListener('popstate', handlePopState);
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      // Store references for cleanup
      this._navigationListeners = {
        popstate: handlePopState,
        beforeunload: handleBeforeUnload
      };
      
      console.log('✅ Navigation detection listeners set up');
      
    } catch (error) {
      console.warn('Failed to set up navigation detection:', error);
    }
  }

  /**
   * Remove navigation event listeners
   */
  removeNavigationDetection() {
    if (typeof window === 'undefined' || !this._navigationListeners) {
      return;
    }
    
    try {
      window.removeEventListener('popstate', this._navigationListeners.popstate);
      window.removeEventListener('beforeunload', this._navigationListeners.beforeunload);
      
      delete this._navigationListeners;
      console.log('✅ Navigation detection listeners removed');
      
    } catch (error) {
      console.warn('Failed to remove navigation detection:', error);
    }
  }

  /**
   * Prevent other scroll operations during restoration
   * @param {number} durationMs - Duration to prevent interference
   */
  preventScrollInterference(durationMs = 1000) {
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      // Add a flag to prevent interference
      this._preventingInterference = true;
      
      // Remove the flag after the specified duration
      setTimeout(() => {
        this._preventingInterference = false;
        console.log('Scroll interference prevention disabled');
      }, durationMs);
      
      console.log('Scroll interference prevention enabled for', durationMs, 'ms');
      
    } catch (error) {
      console.warn('Failed to prevent scroll interference:', error);
    }
  }

  /**
   * Check if scroll interference prevention is active
   */
  isPreventingScrollInterference() {
    return this._preventingInterference === true;
  }

  /**
   * Prevent automatic scroll to top during cache restoration
   * @param {number} durationMs - Duration to prevent scroll to top
   */
  preventScrollToTop(durationMs = 3000) {
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      // Store the current scroll position
      const currentScroll = this.getCurrentScrollPosition();
      
      // Add a flag to prevent scroll to top
      this._preventingScrollToTop = true;
      
      // Set up a watcher to restore scroll if it gets changed to 0
      let scrollWatcher;
      let checkCount = 0;
      const maxChecks = 60; // Check 60 times over the duration (more frequent)
      const checkInterval = durationMs / maxChecks;
      
      scrollWatcher = setInterval(() => {
        const currentY = window.scrollY;
        
        // If scroll position is 0 and we have a saved position, restore it immediately
        if (currentY === 0 && currentScroll.y > 0 && this._preventingScrollToTop) {
          console.log('🔄 Preventing scroll to top, restoring position:', currentScroll.y);
          window.scrollTo({
            left: currentScroll.x,
            top: currentScroll.y,
            behavior: 'instant'
          });
          
          // Also prevent any further scroll to top attempts
          this.preventScrollToTop(durationMs - (checkCount * checkInterval));
        }
        
        checkCount++;
        if (checkCount >= maxChecks) {
          clearInterval(scrollWatcher);
          this._preventingScrollToTop = false;
          console.log('Scroll to top prevention disabled');
        }
      }, checkInterval);
      
      console.log('Scroll to top prevention enabled for', durationMs, 'ms, current position:', currentScroll.y);
      
    } catch (error) {
      console.warn('Failed to prevent scroll to top:', error);
    }
  }

  /**
   * Check if scroll to top prevention is active
   */
  isPreventingScrollToTop() {
    return this._preventingScrollToTop === true;
  }

  /**
   * Temporarily disable automatic scrolling to maintain saved position
   * @param {number} durationMs - Duration to disable auto-scrolling
   */
  disableAutoScrolling(durationMs = 3000) {
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      // Store original scroll behavior
      const originalScrollBehavior = document.documentElement.style.scrollBehavior;
      
      // Disable smooth scrolling temporarily
      document.documentElement.style.scrollBehavior = 'auto';
      
      // Add a flag to prevent auto-scrolling
      this._autoScrollingDisabled = true;
      
      // Restore after duration
      setTimeout(() => {
        document.documentElement.style.scrollBehavior = originalScrollBehavior;
        this._autoScrollingDisabled = false;
        console.log('Auto-scrolling re-enabled');
      }, durationMs);
      
      console.log('Auto-scrolling disabled for', durationMs, 'ms');
      
    } catch (error) {
      console.warn('Failed to disable auto-scrolling:', error);
    }
  }

  /**
   * Check if auto-scrolling is disabled
   */
  isAutoScrollingDisabled() {
    return this._autoScrollingDisabled === true;
  }

  /**
   * Save the position of a specific product card for precise restoration
   * @param {string} cacheKey - Cache key for this product list
   * @param {number} page - Page number
   * @param {string} productId - ID of the clicked product
   * @param {Object} productPosition - Position data for the product
   */
  saveProductCardPosition(cacheKey, page, productId, productPosition) {
    if (typeof window === 'undefined') {
      return false;
    }
    
    try {
      const cache = this.getCache();
      
      if (cache[cacheKey] && cache[cacheKey][page]) {
        // Initialize product positions if not exists
        if (!cache[cacheKey][page].productPositions) {
          cache[cacheKey][page].productPositions = {};
        }
        
        // Save the product position
        cache[cacheKey][page].productPositions[productId] = {
          ...productPosition,
          timestamp: Date.now()
        };
        
        // Save to sessionStorage
        sessionStorage.setItem(this.cacheKey, JSON.stringify(cache));
        
        console.log(`📍 Product card position saved: ${productId} at y=${productPosition.y}`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to save product card position:', error);
      return false;
    }
  }

  /**
   * Get the saved position of a specific product card
   * @param {string} cacheKey - Cache key for this product list
   * @param {number} page - Page number
   * @param {string} productId - ID of the product
   */
  getProductCardPosition(cacheKey, page, productId) {
    if (typeof window === 'undefined') {
      return null;
    }
    
    try {
      const cache = this.getCache();
      
      if (cache[cacheKey] && 
          cache[cacheKey][page] && 
          cache[cacheKey][page].productPositions && 
          cache[cacheKey][page].productPositions[productId]) {
        
        const position = cache[cacheKey][page].productPositions[productId];
        console.log(`📍 Product card position found: ${productId} at y=${position.y}`);
        return position;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get product card position:', error);
      return null;
    }
  }

  /**
   * Clear product positions for a specific cache entry
   * @param {string} cacheKey - Cache key for this product list
   * @param {number} page - Page number (optional, if not provided clears all pages)
   */
  clearProductPositions(cacheKey, page = null) {
    if (typeof window === 'undefined') {
      return false;
    }
    
    try {
      const cache = this.getCache();
      
      if (cache[cacheKey]) {
        if (page !== null) {
          // Clear positions for specific page
          if (cache[cacheKey][page] && cache[cacheKey][page].productPositions) {
            delete cache[cacheKey][page].productPositions;
            sessionStorage.setItem(this.cacheKey, JSON.stringify(cache));
            console.log(`🗑️ Cleared product positions for ${cacheKey}, page ${page}`);
            return true;
          }
        } else {
          // Clear positions for all pages in this cache key
          let cleared = false;
          Object.keys(cache[cacheKey]).forEach(pageNum => {
            if (cache[cacheKey][pageNum].productPositions) {
              delete cache[cacheKey][pageNum].productPositions;
              cleared = true;
            }
          });
          if (cleared) {
            sessionStorage.setItem(this.cacheKey, JSON.stringify(cache));
            console.log(`🗑️ Cleared all product positions for ${cacheKey}`);
            return true;
          }
        }
      }
      
      return false;
    } catch (error) {
      console.error('Failed to clear product positions:', error);
      return false;
    }
  }

  /**
   * Restore scroll position to a specific product card
   * @param {string} cacheKey - Cache key for this product list
   * @param {number} page - Page number
   * @param {string} productId - ID of the product to scroll to
   * @param {number} delayMs - Delay before restoring scroll
   */
  restoreToProductCard(cacheKey, page, productId, delayMs = 500) {
    if (typeof window === 'undefined') {
      return false;
    }
    
    try {
      const productPosition = this.getProductCardPosition(cacheKey, page, productId);
      
      if (!productPosition) {
        console.log(`❌ No saved position found for product: ${productId}`);
        return false;
      }

      // Validate that product element exists in DOM
      const productElement = document.querySelector(`[data-product-id="${productId}"]`);
      if (!productElement) {
        console.log(`❌ Product element not found in DOM for ID: ${productId}, clearing saved position`);
        // Clear the invalid position
        this.clearProductPositions(cacheKey, page);
        return false;
      }

      // Check if position is too old (more than 1 hour)
      const positionAge = Date.now() - (productPosition.timestamp || 0);
      const maxAge = 60 * 60 * 1000; // 1 hour
      if (positionAge > maxAge) {
        console.log(`❌ Product position too old (${Math.round(positionAge / 1000 / 60)} minutes), clearing saved position`);
        // Clear the expired position
        this.clearProductPositions(cacheKey, page);
        return false;
      }
      
      // Prevent interference during restoration
      this.preventScrollInterference(2000);
      this.preventScrollToTop(3000);
      this.disableAutoScrolling(3000);
      
      console.log(`🎯 Restoring scroll to product card: ${productId} at y=${productPosition.y}`);
      
      // Wait for the page to load and then scroll to the product
      setTimeout(() => {
        // First scroll to the general page position
        if (productPosition.pageScrollY) {
          window.scrollTo({
            left: productPosition.pageScrollX || 0,
            top: productPosition.pageScrollY,
            behavior: 'instant'
          });
        }
        
        // Then scroll to the specific product card
        if (productPosition.y) {
          window.scrollTo({
            left: productPosition.x || 0,
            top: productPosition.y,
            behavior: 'instant'
          });
        }
        
        console.log(`✅ Scroll restored to product card: ${productId}`);
      }, delayMs);
      
      return true;
    } catch (error) {
      console.error('Failed to restore to product card:', error);
      return false;
    }
  }
}

// Export singleton instance
export const productListCache = new ProductListCacheManager();

// Export class for testing or custom instances
export { ProductListCacheManager };

// Safe export for server-side rendering
export const safeProductListCache = (() => {
  try {
    return new ProductListCacheManager();
  } catch (error) {
    console.warn('ProductListCacheManager failed to initialize, using mock instance');
    // Return a mock instance with safe methods
    return {
      saveToCache: () => false,
      getFromCache: () => null,
      getFromCacheWithScroll: () => null,
      getCompleteCachedData: () => null,
      smartCacheRetrieval: () => ({ success: false, reason: 'Mock instance' }),
      generateCacheKey: () => 'mock_key',
      removeFromCache: () => {},
      clearCacheByKey: () => {},
      clearAllCache: () => {},
      clearCachePreserveProductPositions: () => {},
      getCacheStats: () => null,
      isCacheAvailable: () => false,
      filtersMatch: () => false,
      isInitialized: () => false,
      getStatus: () => ({ isClient: false, isInitialized: false, isCacheAvailable: false }),
      testCache: () => ({ success: false, error: 'Mock instance - no functionality' }),
      getCurrentScrollPosition: () => ({ x: 0, y: 0 }),
      getContainerScrollPosition: () => ({ x: 0, y: 0 }),
      restoreScrollPosition: () => {},
      updateScrollPosition: () => false,
      saveCurrentScrollPosition: () => false,
      setupScrollSaving: () => {},
      removeScrollSaving: () => {},
      saveScrollBeforeNavigation: () => {},
      oneClickRestore: () => ({ success: false, reason: 'Mock instance - no functionality' }),
      hasValidCache: () => false,
      shouldUseCache: () => false,
      getCacheInfo: () => ({ exists: false, reason: 'Mock instance' }),
      debugCache: () => ({ error: 'Mock instance - no functionality' }),
      manualClearCache: () => false,
      shouldPreserveCache: () => true,
      markAsNavigation: () => {},
      setupNavigationDetection: () => {},
      removeNavigationDetection: () => {},
      preventScrollInterference: () => {},
      isPreventingScrollInterference: () => false,
      preventScrollToTop: () => {},
      isPreventingScrollToTop: () => false,
      disableAutoScrolling: () => {},
      isAutoScrollingDisabled: () => false,
      saveProductCardPosition: () => false,
      getProductCardPosition: () => null,
      restoreToProductCard: () => false,
      clearProductPositions: () => false,
      detectBrowserReload: () => false,
      detectHardRefreshImproved: () => {},
      isPageReadyForScroll: () => false
    };
  }
})();
